/**
 * Upward Routing
 * 
 * Back-edge case: target is above source (feedback loops)
 * Connection: top of source → bottom of target
 */

import { NODE_WIDTH, NODE_HEIGHT, CORNER_RADIUS, MIN_PERPENDICULAR_DISTANCE, ROUTING_OBSTACLE_INFLATION } from '../constants';
import { buildPathWithCorners } from '../pathBuilders';
import { filterRedundantPoints } from './repairPath';

const EXIT_CLEARANCE = MIN_PERPENDICULAR_DISTANCE + CORNER_RADIUS;

function areNodesAligned(fromCenterX, toCenterX, threshold = 30) {
  return Math.abs(fromCenterX - toCenterX) < threshold;
}

function isPathBlocked(midX, startY, endY, obstacles) {
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeLeft = node.pos.x;
    const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH);
    const nodeTop = node.pos.y;
    const nodeBottom = node.pos.y + (node.dims.height || NODE_HEIGHT);
    
    if (midX > nodeLeft - ROUTING_OBSTACLE_INFLATION && midX < nodeRight + ROUTING_OBSTACLE_INFLATION) {
      if (nodeTop < startY && nodeBottom > endY) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Route upward (back-edge case: target above source)
 */
export function routeUpward(startX, startY, endX, endY, fromPos, toPos, fromDims, toDims, obstacles, edgeId = null, laneAssignment = null) {
  const fromWidth = fromDims?.width || NODE_WIDTH;
  const toWidth = toDims?.width || NODE_WIDTH;
  
  const fromCenterX = fromPos.x + fromWidth / 2;
  const toCenterX = toPos.x + toWidth / 2;
  const horizontalDiff = Math.abs(fromCenterX - toCenterX);
  
  if (areNodesAligned(fromCenterX, toCenterX)) {
    const midX = (startX + endX) / 2;
    
    if (!isPathBlocked(midX, startY, endY, obstacles)) {
      let points;
      if (Math.abs(startX - endX) > 0.5) {
        const midY = (startY + endY) / 2;
        points = [
          { x: startX, y: startY },
          { x: startX, y: midY },
          { x: endX, y: midY },
          { x: endX, y: endY },
        ];
      } else {
        points = [
          { x: startX, y: startY },
          { x: endX, y: endY },
        ];
      }
      return buildPathWithCorners(filterRedundantPoints(points), CORNER_RADIUS);
    }
  }
  
  // Route around
  let goRight = endX > startX;
  if (horizontalDiff < fromWidth / 2) {
    const spaceOnLeft = Math.min(fromPos.x, toPos.x);
    const spaceOnRight = 1000 - Math.max(fromPos.x + fromWidth, toPos.x + toWidth);
    goRight = spaceOnRight > spaceOnLeft;
  }
  
  let outerX;
  if (goRight) {
    const rightEdge = Math.max(fromPos.x + fromWidth, toPos.x + toWidth);
    outerX = rightEdge + ROUTING_OBSTACLE_INFLATION * 2;
  } else {
    const leftEdge = Math.min(fromPos.x, toPos.x);
    outerX = leftEdge - ROUTING_OBSTACLE_INFLATION * 2;
  }
  
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeLeft = node.pos.x - ROUTING_OBSTACLE_INFLATION;
    const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH) + ROUTING_OBSTACLE_INFLATION;
    if (goRight && outerX < nodeRight && outerX > nodeLeft) {
      outerX = nodeRight + ROUTING_OBSTACLE_INFLATION;
    } else if (!goRight && outerX > nodeLeft && outerX < nodeRight) {
      outerX = nodeLeft - ROUTING_OBSTACLE_INFLATION;
    }
  }
  
  let exitY = startY - EXIT_CLEARANCE;
  let entryY = endY + EXIT_CLEARANCE;
  
  const minHorizX = Math.min(startX, outerX, endX);
  const maxHorizX = Math.max(startX, outerX, endX);
  
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeLeft = node.pos.x;
    const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH);
    const nodeTop = node.pos.y;
    const nodeBottom = node.pos.y + (node.dims.height || NODE_HEIGHT);
    
    const horizontalOverlap = nodeRight > minHorizX - ROUTING_OBSTACLE_INFLATION && nodeLeft < maxHorizX + ROUTING_OBSTACLE_INFLATION;
    const verticallyBetween = nodeBottom > endY && nodeTop < startY;
    
    if (horizontalOverlap && verticallyBetween) {
      if (exitY > nodeTop - ROUTING_OBSTACLE_INFLATION && exitY < nodeBottom + ROUTING_OBSTACLE_INFLATION) {
        exitY = nodeTop - ROUTING_OBSTACLE_INFLATION * 2;
      }
      if (entryY > nodeTop - ROUTING_OBSTACLE_INFLATION && entryY < nodeBottom + ROUTING_OBSTACLE_INFLATION) {
        entryY = nodeBottom + ROUTING_OBSTACLE_INFLATION * 2;
      }
    }
  }
  
  exitY = Math.min(exitY, startY - EXIT_CLEARANCE);
  entryY = Math.max(entryY, endY + EXIT_CLEARANCE);
  
  const points = [
    { x: startX, y: startY },
    { x: startX, y: exitY },
    { x: outerX, y: exitY },
    { x: outerX, y: entryY },
    { x: endX, y: entryY },
    { x: endX, y: endY },
  ];
  
  return buildPathWithCorners(filterRedundantPoints(points), CORNER_RADIUS);
}
